<script>
  // Plays a SoundCloud track through the widget API in a hidden iframe and
  // exposes the same bindings the <audio> element used to: time, duration, paused.
  import { onMount } from 'svelte';

  export let url;
  export let time = 0;
  export let duration = 0;
  export let paused = true;

  let iframe;
  let widget = null;
  let seekTarget = null;
  let seekTs = 0;

  $: widgetUrl =
    'https://w.soundcloud.com/player/?url=' +
    encodeURIComponent(url) +
    '&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&visual=false';

  // The widget keeps reporting the old position for a moment after a seek; ignore those.
  function acceptTime(t) {
    if (seekTarget === null) return true;
    if (Date.now() - seekTs < 1500 && Math.abs(t - seekTarget) > 3) return false;
    seekTarget = null;
    return true;
  }

  export function seek(seconds) {
    if (!widget) return;
    const t = Math.max(0, duration ? Math.min(seconds, duration) : seconds);
    seekTarget = t;
    seekTs = Date.now();
    time = t;
    widget.seekTo(t * 1000);
    if (paused) widget.play();
  }

  export function toggle() {
    if (widget) widget.toggle();
  }

  function init() {
    const Events = window.SC.Widget.Events;
    widget = window.SC.Widget(iframe);
    widget.bind(Events.READY, () => {
      widget.getDuration(ms => (duration = ms / 1000));
    });
    widget.bind(Events.PLAY_PROGRESS, e => {
      const t = e.currentPosition / 1000;
      if (acceptTime(t)) time = t;
    });
    widget.bind(Events.PLAY, () => (paused = false));
    widget.bind(Events.PAUSE, () => (paused = true));
    widget.bind(Events.FINISH, () => (paused = true));
  }

  onMount(() => {
    if (window.SC && window.SC.Widget) {
      init();
      return;
    }
    const tag = document.createElement('script');
    tag.src = 'https://w.soundcloud.com/player/api.js';
    tag.onload = init;
    document.head.appendChild(tag);
  });
</script>

<style>
  .player {
    position: fixed;
    left: 1em;
    bottom: 1em;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 0.75em;
    font-size: 0.8em;
  }

  button {
    width: 2.5em;
    height: 2.5em;
    border-radius: 50%;
    border: 1px solid #fff;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    cursor: pointer;
  }

  button:hover {
    border-color: #d0ff00;
    color: #d0ff00;
  }

  a {
    color: #fff;
    opacity: 0.7;
  }

  a:hover {
    opacity: 1;
  }

  iframe {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
</style>

<div class="player">
  <button on:click="{toggle}" aria-label="{paused ? 'Play' : 'Pause'}">
    {paused ? '▶' : '❚❚'}
  </button>
  <a href="{url}" target="_blank" rel="noopener noreferrer">SoundCloud ↗</a>
  <iframe
    bind:this="{iframe}"
    src="{widgetUrl}"
    title="SoundCloud player"
    allow="autoplay"
    frameborder="0"
    aria-hidden="true"
    tabindex="-1"></iframe>
</div>
